import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { X, Loader2, Info } from "lucide-react";
import { COMMUNITY_KINDS, reportPlace } from "../lib/community";

/** Three taps: open, pick a type, save. Everything else is optional. */
export default function ReportParkingSheet({ open, onClose, pos, onReported }) {
  const [kind, setKind] = React.useState(null);
  const [schedule, setSchedule] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    if (!kind) return;
    if (!pos) return toast.error("We need your location to place the report.");
    setBusy(true);
    try {
      await reportPlace({
        kind,
        lat: Number(pos.lat.toFixed(6)),
        lng: Number(pos.lng.toFixed(6)),
        schedule: schedule.trim() || null,
      });
      toast.success("Thanks — other drivers can now check it on the map.");
      setKind(null);
      setSchedule("");
      onReported?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the report.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[2000] bg-black/60"
            data-testid="report-parking-backdrop"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 280 }}
            className="fixed bottom-0 left-0 right-0 z-[2001] bg-[#0f0f11] border-t border-white/10 rounded-t-3xl p-5 pb-8"
            style={{ paddingBottom: "calc(2rem + env(safe-area-inset-bottom, 0px))" }}
            data-testid="report-parking-sheet"
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading text-lg font-semibold">Report parking here</h3>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  Permanent parking info at your current location
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center"
                data-testid="report-parking-close"
              >
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2" data-testid="report-parking-kinds">
              {COMMUNITY_KINDS.map((k) => (
                <button
                  key={k.key}
                  type="button"
                  onClick={() => setKind(k.key)}
                  className={`h-12 rounded-xl text-sm font-medium border transition-colors ${
                    kind === k.key
                      ? "bg-[#ccff00] text-[#0a0a0c] border-transparent"
                      : "bg-[#141416] text-zinc-300 border-white/10"
                  }`}
                  data-testid={`report-parking-kind-${k.key}`}
                >
                  {k.label}
                </button>
              ))}
            </div>

            <Input
              value={schedule}
              onChange={(e) => setSchedule(e.target.value)}
              placeholder="Hours on the sign (optional)"
              className="mt-3 h-12 bg-[#141416] border-white/10 text-white"
              data-testid="report-parking-schedule"
            />

            <p className="mt-3 text-[11px] text-zinc-500 flex items-start gap-2">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Your report appears under Layers → Needs checking until other drivers confirm it.
              It never replaces the signs on site.
            </p>

            <Button
              onClick={submit}
              disabled={!kind || busy}
              className="mt-4 w-full h-13 py-3.5 bg-[#ccff00] text-[#0a0a0c] font-semibold rounded-full disabled:opacity-60"
              data-testid="report-parking-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send for review"}
            </Button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
